import * as React from "react";
import { Modal, Tabs } from "antd";
import { useFormContext, useFieldArray } from "react-hook-form";
import { NGLabelText } from "../../../newcomponents/text/labelText";
import { v4 as uuidv4 } from "uuid";
import { CreateNGAdvancedPort } from "./create_ng_advanced_port";
import { AlertCircle } from "react-feather";
import { DomainInput } from "./types/input";
import { getDefaultDomainPortInput } from "./utils/utils";

type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

const suggestedPorts = [443, 80, 514, 1433, 1443, 3000, 3001, 3002, 3003, 3004, 5912, 8443, 8444, 50051, 50052, 5912];

interface Props {}
export const CreateNGAdvancedPorts: React.FC<Props> = () => {
  const { control, watch, setValue } = useFormContext<DomainInput>();

  const { append: appendPort, remove: removePort } = useFieldArray({
    control,
    name: "advanced.spec.ports",
  });

  const dnsMode = watch("advanced.spec.dnsMode");
  const routingMode = watch("advanced.spec.routingMode");
  const certChallengeType = watch("advanced.spec.certChallengeType");

  const gvcName = watch("gvc.name");
  const workloadName = watch("advanced.spec.workload.name");

  const dryRun = watch("dryRun.raw.value") || {};
  const dryRunNameOnly = watch("dryRun.nameOnly.value") || {};
  const ports = watch("advanced.spec.ports") || [];
  const activeKey = watch("portActiveKey") || ports[0]?.id || "";

  function onEdit(targetKey: React.MouseEvent | React.KeyboardEvent | string, action: "add" | "remove") {
    if (action === "add") {
      add();
    } else {
      Modal.confirm({
        title: "Remove Port",
        content: `Configuration that was made on this port will be lost`,
        onOk() {
          remove(targetKey);
        },
      });
    }
  }

  function add() {
    const newActiveKey = uuidv4();
    const usedPorts = ports.map((p) => p.number);
    let suggestedPort: string | number | undefined = undefined;

    for (const _suggestedPort of suggestedPorts) {
      if (!usedPorts.includes(String(_suggestedPort))) {
        suggestedPort = _suggestedPort;
        break;
      }
    }
    if (!suggestedPort) {
      suggestedPort = usedPorts.toSorted().toReversed()[0] + 1;
    }

    appendPort({
      ...getDefaultDomainPortInput(),
      id: newActiveKey,
      number: String(suggestedPort),
    });
  }

  function remove(targetKey: TargetKey) {
    let newActiveKey = activeKey;
    let lastIndex = -1;
    let removeIndex = -1;

    ports.forEach((item, i) => {
      if (item.id === targetKey) {
        lastIndex = i - 1;
        removeIndex = i;
      }
    });

    if (removeIndex >= 0) {
      removePort(removeIndex);

      if (ports.length > 1 && newActiveKey === targetKey) {
        if (lastIndex >= 0) {
          newActiveKey = ports[lastIndex].id!;
        } else {
          newActiveKey = ports[removeIndex + 1]?.id || ports[0]?.id || "";
        }
        setValue("portActiveKey", newActiveKey);
      }
    }
  }

  function onChange(newActiveKey: string) {
    setValue("portActiveKey", newActiveKey);
  }

  if (routingMode === "none") {
    return null;
  }

  if (routingMode === "workloadSubdomain" && (!gvcName || !workloadName)) {
    return null;
  }

  if (dryRunNameOnly.status !== 200) {
    return null;
  }

  return (
    <>
      <NGLabelText className="cursor-default">Ports</NGLabelText>
      <Tabs
        type={"editable-card"}
        activeKey={activeKey}
        items={ports.map((p, i) => {
          const issues = ((dryRun.details as any)?.issues || []) as { path: string }[];

          let hasError = issues.some((i) => i.path.includes(`spec.ports[${i}]`));

          // need to manually check secret req here
          if (
            dnsMode === "cname" &&
            routingMode === "gvcSubdomain" &&
            certChallengeType === "http01" &&
            (p.tls.isEnabled || ((!p.number || p.number === "443") && p.protocol !== "tcp")) &&
            !p.tls?.serverCertificate?.secret.name
          ) {
            hasError = true;
          }

          return {
            key: p.id!,
            label: (
              <div className="flex items-center">
                <div>{`${p?.number} ${p?.protocol?.toUpperCase()}`}</div>
                {hasError ? <AlertCircle className="ml-1 h-4 feather-icon color-danger portError" /> : null}
              </div>
            ),
            closable: ports.length > 1,
            children: <CreateNGAdvancedPort id={p.id!} />,
          };
        })}
        onChange={onChange}
        onEdit={onEdit}
      />
    </>
  );
};
